/**
 * Curated icon set for session categories.
 *
 * Each entry is the literal Lucide icon name (matching the named export in
 * both `lucide-react` for admin and `lucide-react-native` for mobile) plus
 * a short display label shown in the admin picker. The set is intentionally
 * small (~25 entries) so the organizer browses by sight in a single
 * scrollable dropdown; if a category needs an icon outside this list, the
 * fix is to add one entry here and rebuild rather than letting admins type
 * arbitrary names that mobile can't render.
 *
 * Storage in `SessionTypeConfig.iconName` is permissive (`string | null`)
 * so older docs without the field and forward-compatible adds aren't
 * runtime errors. Renderers fall back to `Calendar` (the default) when the
 * stored name isn't in this map.
 */
export interface SessionTypeIcon {
  /** Exact Lucide icon name. */
  name: string;
  /** Short human label for the admin picker. */
  label: string;
}

export const SESSION_TYPE_ICONS: SessionTypeIcon[] = [
  { name: 'Calendar', label: 'Calendar (default)' },
  { name: 'Presentation', label: 'Presentation' },
  { name: 'BarChart3', label: 'Charts / Data' },
  { name: 'Mic', label: 'Keynote / Speaker' },
  { name: 'MessageCircle', label: 'Q&A / Discussion' },
  { name: 'Users', label: 'Networking / Group' },
  { name: 'Briefcase', label: 'Business Meeting' },
  { name: 'BookOpen', label: 'Workshop' },
  { name: 'GraduationCap', label: 'Training' },
  { name: 'Lightbulb', label: 'Innovation' },
  { name: 'Coffee', label: 'Break' },
  { name: 'Utensils', label: 'Meal' },
  { name: 'Wine', label: 'Reception / Cocktail' },
  { name: 'PartyPopper', label: 'Party / Celebration' },
  { name: 'Music', label: 'Entertainment' },
  { name: 'Award', label: 'Awards' },
  { name: 'Plane', label: 'Flight / Travel' },
  { name: 'Hotel', label: 'Lodging' },
  { name: 'Car', label: 'Ground Transport' },
  { name: 'Bus', label: 'Shuttle' },
  { name: 'MapPin', label: 'Tour / Outing' },
  { name: 'Mountain', label: 'Outdoor / Skiing' },
  { name: 'Sun', label: 'Outdoor Day Event' },
  { name: 'Dumbbell', label: 'Fitness' },
  { name: 'Heart', label: 'Wellness / Spa' },
];

/** Valid icon names as a string-literal union of the curated set. */
export const SESSION_TYPE_ICON_NAMES: string[] = SESSION_TYPE_ICONS.map(
  (i) => i.name,
);

/** Default icon name used when `iconName` is null or unknown. */
export const DEFAULT_SESSION_TYPE_ICON = 'Calendar';
